import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { expiryDate } from "../../../common/components/Helper/Utils";
import constants from "../../../utils/constants";
import {
  getclientandsecret,
  generateaccesstoken,
  sendrequestapi,
  sendinventoryapi,
} from "./actions/administration";

const TOKEN_TTL_SECONDS = 15 * 60;

const API_ENDPOINTS = [
  { label: "v1/products", method: "POST" },
  { label: "v1/inventories", method: "POST" },
  // { label: "/inventory", method: "GET" },
];

const V1_PRODUCTS_REQUEST_BODY = `{
    "key": "rolex", "limit": 10, "page_no": 1 
}`;
const V1_INVENTORIES_REQUEST_BODY = `{
    "status": "ALL", "type": "ALL", "limit": 10, "page_no": 1
}`;
function SectionCard({ title, children, sx = {} }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "14px",
        backgroundColor: "#FFFFFF",
        boxShadow: "0px 6px 20px rgba(17, 24, 39, 0.08)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "1.35rem", fontWeight: 500, color: "#111827", mb: 2 }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function CopyableField({
  label,
  value,
  isSecret,
  isVisible,
  onToggleVisibility,
  onCopy,
  isCopied,
}) {
  const labelSx = { color: "#6B7280 !important", fontSize: "14px !important" };

  return (
    <Stack spacing={0.75}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        fullWidth
        size="small"
        value={
          isSecret && !isVisible
            ? "•".repeat(Math.max(value.length, 30))
            : value
        }
        inputProps={{ readOnly: true, style: { fontFamily: "monospace" } }}
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: "9px",
            backgroundColor: "#FFFFFF",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isSecret ? (
                <IconButton size="small" onClick={onToggleVisibility}>
                  {isVisible ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null}
              <Tooltip title={isCopied ? "Copied to Clipboard !" : "Copy"}>
                <IconButton size="small" onClick={onCopy}>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
}

function formatRemaining(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function ApiHubComponent() {
  const theme = useTheme();
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [tokenExpiresIn, setTokenExpiresIn] = useState(0);
  const [endpoint, setEndpoint] = useState("");
  const [method, setMethod] = useState("POST");
  const [response, setResponse] = useState("// Response will appear here");
  const [apiPermissions, setApiPermissions] = useState({});
  const [logs, setLogs] = useState([
    { time: "2m ago", method: "GET", endpoint: "/product", status: "200 OK" },
    {
      time: "1m ago",
      method: "POST",
      endpoint: "/orders",
      status: "201 Created",
    },
    {
      time: "Just now",
      method: "GET",
      endpoint: "/inventory",
      status: "200 OK",
    },
  ]);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [loadingToken, setLoadingToken] = useState(false);
  const [expiresOn, setExpiresOn] = useState(null);

  // const accountId = window.sessionStorage.getItem("accountId");
  const useraccounts = window.sessionStorage.getItem("userDetails")
    ? JSON.parse(window.sessionStorage.getItem("userDetails"))
    : null;
  const businessName = useraccounts?.accountName || "";

  // const clientId = "wcps_client_1a2b3c4d5e6f7g8h9i0j";
  // const clientSecret = "sk_live_02bafeea1fd9421f967b77c26";
  const labelSx = { color: "#6B7280 !important", fontSize: "14px !important" };

  // useEffect(() => {
  //   if (!tokenExpiresIn) return undefined;
  //   const timer = setInterval(() => {
  //     setTokenExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
  //   }, 1000);
  //   return () => clearInterval(timer);
  // }, [tokenExpiresIn]);

  useEffect(() => {
    if (!copiedKey) return undefined;
    const reset = setTimeout(() => setCopiedKey(""), 1000);
    return () => clearTimeout(reset);
  }, [copiedKey]);

  // Fetch client credentials on component mount
  useEffect(() => {
    const fetchClientCredentials = async () => {
      try {
        const accountId = useraccounts.accountId;
        // window.sessionStorage.getItem("accountId");

        // console.log("Fetching client credentials for accountId:", accountId);
        if (accountId) {
          await getclientandsecret(accountId, (response) => {
            // console.log("API response for client credentials:", response);
            if (response && response.success === true && response.data) {
              setApiPermissions(response.data);
              setClientId(response.data.client_id || "");
              // console.log("Client ID fetched:", response.data.client_id);
              setClientSecret(response.data.client_secret || "");
              // console.log("Client Secret fetched:", response.data.client_secret);
              if (response.data.accesstoken) {
                setAccessToken(response.data.accesstoken);
              }

              if (response.data.exiprydate) {
                const expiryTime = new Date(response.data.exiprydate).getTime();
                const currentTime = new Date().getTime();
                const expiresIn = Math.floor((expiryTime - currentTime) / 1000);

                setExpiresOn(response.data.exiprydate);
                setTokenExpiresIn(expiresIn);
              }
            } else {
              console.error("Failed to fetch client credentials:", response);
            }
            setLoadingCredentials(false);
          });
        } else {
          console.error("No accountId found in session storage");
          setLoadingCredentials(false);
        }
      } catch (error) {
        console.error("Error fetching client credentials:", error);
        setLoadingCredentials(false);
      }
    };

    fetchClientCredentials();
  }, []);

  const isProductsExposed = apiPermissions?.is_products_apiexposed === true;

  const isInventoriesExposed =
    apiPermissions?.is_inventories_apiexposed === true;

  const API_ENDPOINTS = useMemo(() => {
    const endpoints = [];
    if (isProductsExposed) {
      endpoints.push({ label: "v1/products", method: "POST" });
    }
    if (isInventoriesExposed) {
      endpoints.push({ label: "v1/inventories", method: "POST" });
    }
    return endpoints;
  }, [isProductsExposed, isInventoriesExposed]);

  useEffect(() => {
    if (API_ENDPOINTS.length > 0 && !endpoint) {
      setEndpoint(API_ENDPOINTS[0].label);
      setMethod(API_ENDPOINTS[0].method);
    }
  }, [API_ENDPOINTS]);

  const activeToken = Boolean(
    accessToken && expiresOn && new Date(expiresOn) > new Date(),
  );

  const tokenStatusLabel = useMemo(() => {
    if (!activeToken) return "No active token";
    return `Expires on ${formatRemaining(tokenExpiresIn)}`;
  }, [activeToken, tokenExpiresIn]);

  const copyText = async (key, value) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement("input");
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopiedKey(key);
    } catch (err) {
      setCopiedKey("");
    }
  };

  const handleGenerateToken = async () => {
    if (!clientId || !clientSecret) {
      setResponse("// Client ID or Client Secret is missing.");
      return;
    }
    setLoadingToken(true);
    setResponse("// Generating access token...");

    try {
      // console.log("Requesting access token with clientId:", clientId, "and clientSecret:", clientSecret);
      await generateaccesstoken(
        {
          client_id: clientId,
          client_secret: clientSecret,
          businessname: useraccounts.accountName,
        },
        (response) => {
          // console.log("API response for access token generation:", response);
          if (
            response &&
            (response.success === 1 || response.success === true) &&
            response.data
          ) {
            // if (response && response.data.access_token) {
            // console.log("Access token generated:", response.data.access_token);
            const token = response.data.access_token;
            // console.log("Token value:", token);
            if (response.data.expires_in) {
              setExpiresOn(response.data.expires_in);
            }
            let expiresIn = TOKEN_TTL_SECONDS;

            if (response.data.expires_in) {
              const expiryTime = new Date(response.data.expires_in).getTime();
              const currentTime = new Date().getTime();
              expiresIn = Math.floor((expiryTime - currentTime) / 1000);
            }

            if (token) {
              setAccessToken(token);
              setTokenExpiresIn(expiresIn);
              setResponse("// Access token generated successfully.");
            } else {
              setResponse(
                JSON.stringify(
                  {
                    message:
                      "Token response received but token field was missing.",
                    response,
                  },
                  null,
                  2,
                ),
              );
            }
          } else {
            setResponse(
              JSON.stringify(response || { error: "No response" }, null, 2),
            );
          }
        },
      );
    } catch (error) {
      setResponse(JSON.stringify({ error: error.message || error }, null, 2));
    } finally {
      setLoadingToken(false);
    }
  };

  const handleSendRequest = () => {
    if (!activeToken) return;
    const payload = {
      key: "rolex",
      limit: 10,
      page_no: 1,
    };
    setResponse("// Sending request...");
    setLogs((prev) => [
      {
        time: "Just now",
        method,
        endpoint,
        status: method === "POST" ? "201 Created" : "200 OK",
      },
      ...prev.slice(0, 4),
    ]);
    if (endpoint === "v1/inventories") {
      sendinventoryapi(payload, accessToken, (err, res) => {
        if (err) {
          console.error(err);
          setResponse(JSON.stringify({ error: "API call failed" }, null, 2));
        } else {
          setResponse(JSON.stringify(res, null, 2));
        }
      });
    } else {
      sendrequestapi(payload, accessToken, (err, res) => {
        if (err) {
          console.error(err);
          setResponse(JSON.stringify({ error: "API call failed" }, null, 2));
        } else {
          setResponse(JSON.stringify(res, null, 2));
        }
      });
    }
  };

  const handleEndpointChange = (nextEndpoint) => {
    setEndpoint(nextEndpoint);
    const matched = API_ENDPOINTS.find((item) => item.label === nextEndpoint);
    if (matched) setMethod(matched.method);
  };

  const getExpiryStatus = (expiresOn) => {
    if (!expiresOn) return { label: "No expiry", color: "default" };

    const expiryTime = new Date(expiresOn).getTime();
    const currentTime = new Date().getTime();
    const formatted = expiryDate(expiresOn);
    // const formatted = new Date(expiresOn).toLocaleString("en-IN", {
    //   hour: "numeric",
    //   minute: "2-digit",
    //   hour12: true,
    //   day: "numeric",
    //   month: "numeric",
    //   year: "numeric",
    // });

    if (expiryTime > currentTime) {
      return {
        label: `Expires on ${formatted}`,
        color: "success",
      };
    } else {
      return {
        label: `Expired on ${formatted}`,
        color: "error",
      };
    }
  };

  const expiryStatus = getExpiryStatus(expiresOn);

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 600, color: "#111827", mb: 0.75 }}
      >
        API Hub
      </Typography>
      <Typography
        sx={{
          color: "#6B7280 !important",
          mb: 3.5,
          fontSize: "14px !important",
        }}
      >
        Manage API credentials, tokens, and test endpoints
      </Typography>

      <Stack spacing={2.25}>
        <SectionCard title="Client Credentials">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CopyableField
                label="Client ID"
                value={clientId}
                onCopy={() => copyText("id", clientId)}
                isSecret={false}
                isVisible
                isCopied={copiedKey === "id"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CopyableField
                label="Business Name"
                value={businessName}
                onCopy={() => copyText("businessName", businessName)}
                isSecret={false}
                isVisible
                isCopied={copiedKey === "businessName"}
              />
            </Grid>
            <Grid item xs={12}>
              <CopyableField
                label="Client Secret"
                value={clientSecret}
                onCopy={() => copyText("secret", clientSecret)}
                isSecret
                isVisible={showSecret}
                onToggleVisibility={() => setShowSecret((prev) => !prev)}
                isCopied={copiedKey === "secret"}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Access Token">
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                disableElevation
                onClick={handleGenerateToken}
                disabled={loadingToken || !clientId || !clientSecret}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  backgroundColor: "#cea05f",
                  "&:hover": {
                    backgroundColor: "#a97142",
                  },
                }}
              >
                {loadingToken ? "Generating..." : "Generate Token"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                disabled={!activeToken || loadingToken}
                onClick={handleGenerateToken}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  color: "#cea05f",
                  borderColor: "#cea05f",
                  display: "none",
                }}
              >
                Refresh Token
              </Button>
              <Chip
                size="small"
                color={expiryStatus.color}
                label={expiryStatus.label}
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            {!activeToken ? (
              <Alert
                icon={<InfoOutlinedIcon fontSize="inherit" />}
                severity="info"
                sx={{ alignItems: "center" }}
              >
                No Active Access Token is available. Click "Generate Token" to
                create a new access token.
              </Alert>
            ) : (
              <Stack spacing={0.75}>
                <Typography sx={labelSx}>Access Token</Typography>
                <TextField
                  size="small"
                  value={accessToken}
                  inputProps={{
                    readOnly: true,
                    style: { fontFamily: "monospace" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copiedKey === "token" ? "Copied to Clipboard !" : "Copy"}>
                          <IconButton
                            size="small"
                            onClick={() => copyText("token", accessToken)}
                          >
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            )}
          </Stack>
        </SectionCard>

        <SectionCard title="API Tester">
          {API_ENDPOINTS.length === 0 ? (
            <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
              No API endpoints are currently enabled for your account.
            </Typography>
          ) : (
            <Stack spacing={1.8}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>Method</Typography>
                    <Select
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      {/* <MenuItem value="GET">GET</MenuItem> */}
                      <MenuItem value="POST">POST</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <FormControl fullWidth size="small">
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>
                      Endpoint
                    </Typography>
                    <Select
                      value={endpoint}
                      onChange={(e) => handleEndpointChange(e.target.value)}
                    >
                      {API_ENDPOINTS.map((item) => (
                        <MenuItem key={item.label} value={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box>
                <Button
                  variant="contained"
                  startIcon={<SendRoundedIcon />}
                  disableElevation
                  onClick={handleSendRequest}
                  disabled={!activeToken}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    backgroundColor: "#cea05f",
                    "&:hover": {
                      backgroundColor: "#a97142",
                    },
                  }}
                >
                  Send Request
                </Button>
              </Box>

              <Box>
                <Typography sx={{ ...labelSx, mb: 0.8 }}>
                  Request body
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={10}
                  value={
                    activeToken
                      ? endpoint === "v1/inventories"
                        ? V1_INVENTORIES_REQUEST_BODY
                        : V1_PRODUCTS_REQUEST_BODY
                      : ""
                  }
                  placeholder={
                    activeToken
                      ? undefined
                      : "Request body will appear after you generate an access token."
                  }
                  inputProps={{
                    readOnly: true,
                    style: { fontFamily: "monospace" },
                  }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography sx={{ ...labelSx, mb: 0.8 }}>Response</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={7}
                  maxRows={10}
                  value={response}
                  inputProps={{
                    readOnly: true,
                    style: { fontFamily: "monospace" },
                  }}
                />
              </Box>

              {!activeToken && (
                <Alert severity="warning" sx={{ alignItems: "center" }}>
                  Please generate an access token before testing API endpoints.
                </Alert>
              )}
            </Stack>
          )}
        </SectionCard>

        <SectionCard title="API Logs" sx={{ display: "none" }}>
          <Stack spacing={1.1}>
            {logs.map((log, idx) => (
              <Box
                key={`${log.endpoint}-${idx}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 0.35,
                }}
              >
                <Typography
                  sx={{ minWidth: 82, color: "#6B7280", fontSize: "0.85rem" }}
                >
                  {log.time}
                </Typography>
                <Chip
                  size="small"
                  label={log.method}
                  sx={{
                    minWidth: 60,
                    backgroundColor: "#E6F0FF",
                    color: "#1D4ED8",
                    fontWeight: 600,
                  }}
                />
                <Typography
                  sx={{ flex: 1, color: "#111827", fontFamily: "monospace" }}
                >
                  {log.endpoint}
                </Typography>
                <Chip
                  size="small"
                  label={log.status}
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            ))}
          </Stack>
        </SectionCard>
      </Stack>

      {/* {copiedKey ? (
        <Typography sx={{ mt: 1.5, color: "#059669", fontSize: "0.8rem" }}>
          Copied {copiedKey} to clipboard.
        </Typography>
      ) : null} */}
    </Box>
  );
}
